import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { everest } from '~/services/serviceInstances';

export type EverestBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

/**
 * The fetch logic itself, callable directly (without the 3-arg `BaseQueryFn`
 * signature) for one-off imperative calls outside RTK Query — e.g. plain
 * async helpers that aren't consumed as React hooks.
 */
export const fetchEverest = async ({
  url,
  method = 'get',
  body,
  params,
  rtkQueryOptions,
}: EverestBaseQueryArg) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
  };

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await everest.get(path, config);
    } else if (m === 'post') {
      result = await everest.post(path, body, config);
    } else if (m === 'patch') {
      result = await everest.patch(path, body, config);
    } else if (m === 'put') {
      result = await everest.put(path, body, config);
    } else if (m === 'delete') {
      result = await everest.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (e) {
    if (isAxiosError(e)) {
      return {
        error: {
          status: e.response?.status ?? 'FETCH_ERROR',
          data: e.response?.data ?? e.message,
          response: e.response
            ? { status: e.response.status, data: e.response.data }
            : undefined,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(e) } };
  }
};

export const everestBaseQuery: BaseQueryFn<
  EverestBaseQueryArg,
  unknown,
  unknown
> = (arg) => fetchEverest(arg);
